import { isStringValue, readVersioned, removeVersioned, writeVersioned } from '@/lib/storage/versioned-storage';
import { isEntryHost } from '@/utils/app-domain';
import { FC, PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { useGlobalStoreSelector } from './global.store';

const PLATFORM_TITLE = 'Vadio';

// localStorage is already origin-scoped, so one key per host needs no hostname in it: a branded
// tenant on childc.vadio.app cannot read what parent.vadio.app cached.
const BRAND_TITLE_STORAGE_KEY = 'app:brand-title';

/** The last branded display name this host resolved to, or null if none is remembered. */
export const readStoredBrandTitle = (): string | null => {
	// The entry host belongs to no workspace, so a name cached there would outlive the session it came from.
	if (isEntryHost()) return null;
	return readVersioned({ key: BRAND_TITLE_STORAGE_KEY, isValid: isStringValue }) || null;
};

const writeStoredBrandTitle = (name: string | null) => {
	if (name && !isEntryHost()) writeVersioned({ key: BRAND_TITLE_STORAGE_KEY, data: name });
	else removeVersioned({ key: BRAND_TITLE_STORAGE_KEY });
};

/**
 * Paints the remembered tenant name into the tab before React renders. `TitleProvider` lives inside
 * `GlobalStoreProvider`, which renders a skeleton (never its children) while hydrate is in flight,
 * so on a white-label host the tab would otherwise read the platform's "Vadio" for the whole boot —
 * the brand only resolves once profile + companies land. Called from `main.tsx` at t=0.
 */
export const applyStoredBrandTitle = () => {
	const stored = readStoredBrandTitle();
	if (stored) document.title = stored;
};

type TitleContextType = {
	setTitle: (title: string | null) => void;
	setPrefixOverride: (prefix: string | null) => void;
};

const TitleContext = createContext<TitleContextType | null>(null);

export const TitleProvider: FC<PropsWithChildren> = ({ children }) => {
	const [title, setTitle] = useState<string | null>(null);
	const [prefixOverride, setPrefixOverride] = useState<string | null>(null);
	// The document title reads the resolved-brand display name (branded workspaces only), never
	// whiteLabeling directly; unbranded workspaces keep the platform title. A null `resolvedBrand`
	// means "not resolved yet" — distinct from a resolved-but-unbranded one, which is why this reads
	// the slot rather than pre-flattening it to a name.
	const resolvedBrand = useGlobalStoreSelector((s) => s.resolvedBrand);
	const brandName = resolvedBrand ? (resolvedBrand.isBranded ? resolvedBrand.displayName : null) : undefined;

	// Only read once: after the first paint the resolved slot is the authority, and re-reading would
	// let a stale cache win over a workspace that just turned its branding off.
	const [cachedBrandName] = useState(readStoredBrandTitle);

	useEffect(() => {
		// Still resolving — keep whatever is remembered rather than overwriting it with the platform name.
		if (brandName === undefined) return;
		writeStoredBrandTitle(brandName);
	}, [brandName]);

	const finalPrefix = prefixOverride || (brandName === undefined ? cachedBrandName : brandName) || PLATFORM_TITLE;

	useEffect(() => {
		document.title = title ? `${finalPrefix} - ${title}` : finalPrefix;
	}, [finalPrefix, title]);

	return (
		<TitleContext.Provider
			value={{
				setTitle,
				setPrefixOverride,
			}}
		>
			{children}
		</TitleContext.Provider>
	);
};

export const useSetTitlePrefix = (prefix: string | null) => {
	const context = useContext(TitleContext);

	if (!context) {
		throw new Error('useSetTitlePrefix must be used within a TitleProvider');
	}

	useEffect(() => {
		context.setPrefixOverride(prefix);
		return () => context.setPrefixOverride(null);
	}, [context, prefix]);
};

export const useSetTitle = (title: string) => {
	const context = useContext(TitleContext);

	if (!context) {
		throw new Error('useSetTitle must be used within a TitleProvider');
	}

	useEffect(() => {
		context.setTitle(title);
	}, [context, title]);
};
